// Multi-Scenario Payment Configuration for JOBENG
// Complete payment scenarios with UK pricing and VAT

// Payment scenarios as an array for easy filtering and mapping
export const PAYMENT_SCENARIOS = [
  // ===== MONTHLY SUBSCRIPTION SCENARIOS =====
  {
    id: 'prof_sub_monthly',
    type: 'subscription',
    name: 'Professional Premium',
    description: 'Access to premium job opportunities and AI matching',
    basePrice: 1400, // £14.00 in pence (before VAT)
    vat: 280, // 20% VAT in pence
    total: 1680, // Total with VAT in pence
    amountInPence: 1680,
    currency: 'GBP',
    interval: 'month',
    icon: 'account-tie',
    popular: true,
    features: [
      'Priority application to premium jobs',
      'Early access to new internship postings', 
      'AI-powered job matching',
      'Professional profile highlighting',
      'Direct messaging with employers',
      'Course and book discounts',
      'Resume optimization tools',
      'Interview preparation resources',
      'Career development guidance',
      'Professional networking access'
    ],
    vatRate: 0.20,
    category: 'subscription'
  },

  {
    id: 'biz_sub_monthly',
    type: 'subscription', 
    name: 'Business Premium',
    description: 'Complete business management and recruitment tools',
    basePrice: 3900, // £39.00 in pence (before VAT)
    vat: 780, // 20% VAT in pence
    total: 4680, // Total with VAT in pence
    amountInPence: 4680,
    currency: 'GBP',
    interval: 'month',
    icon: 'office-building',
    popular: false,
    features: [
      'Unlimited job postings',
      'Access to premium talent pool',
      'Advanced AI matching algorithms',
      'Dedicated account manager',
      'Priority customer support',
      'Analytics dashboard',
      'Team collaboration tools',
      'Course and book discounts',
      'Recruitment workflow automation',
      'Candidate management system',
      'Interview scheduling tools',
      'Compliance management',
      'Financial reporting',
      'Marketing campaign tools'
    ],
    vatRate: 0.20,
    category: 'subscription'
  },

  // ===== ONE-TIME COURSE PURCHASES =====
  {
    id: 'course_engineering',
    type: 'course',
    name: 'Engineering Fundamentals',
    description: 'Professional engineering courses and certifications',
    basePrice: 1500, // £15.00 in pence (before VAT)
    vat: 300, // 20% VAT in pence
    total: 1800, // Total with VAT in pence
    amountInPence: 1800,
    currency: 'GBP',
    icon: 'school',
    popular: true,
    features: [
      'Comprehensive engineering curriculum',
      'Industry-recognized certification',
      'Hands-on practical exercises',
      'Expert instructor support',
      'Lifetime access to materials',
      'Interactive learning modules',
      'Progress tracking',
      'Community forum access'
    ],
    discounts: {
      professional: 500, // £5 discount in pence
      business: 500      // £5 discount in pence
    },
    discountedPrice: 1000, // £10.00 in pence for subscribers
    vatRate: 0.20,
    category: 'course',
    duration: '40 hours',
    level: 'Beginner to Intermediate'
  },

  {
    id: 'course_structural',
    type: 'course',
    name: 'Advanced Structural Analysis',
    description: 'Advanced structural analysis and design course',
    basePrice: 7900, // £79.00 in pence (before VAT)
    vat: 1580, // 20% VAT in pence
    total: 9480, // Total with VAT in pence
    amountInPence: 9480,
    currency: 'GBP',
    icon: 'bridge',
    popular: false,
    features: [
      'Advanced structural analysis techniques',
      'CAD software training included',
      'Real-world project examples',
      'Professional certification',
      'Industry expert mentorship',
      'Software licenses included',
      'Project portfolio development',
      'Job placement assistance'
    ],
    discounts: {
      professional: 2000, // £20 discount in pence
      business: 2500      // £25 discount in pence
    },
    discountedPrice: 5900, // £59.00 in pence for professional subscribers
    vatRate: 0.20,
    category: 'course',
    duration: '120 hours',
    level: 'Advanced'
  },

  {
    id: 'course_project_management',
    type: 'course',
    name: 'Engineering Project Management',
    description: 'Project management for engineering professionals',
    basePrice: 4500, // £45.00 in pence (before VAT)
    vat: 900, // 20% VAT in pence
    total: 5400, // Total with VAT in pence
    amountInPence: 5400,
    currency: 'GBP',
    icon: 'clipboard-list',
    popular: true,
    features: [
      'PMP certification preparation',
      'Agile and Scrum methodologies',
      'Risk management strategies',
      'Budget and timeline planning',
      'Team leadership skills',
      'Quality assurance processes'
    ],
    discounts: {
      professional: 900, // £9 discount in pence
      business: 1350     // £13.50 discount in pence
    },
    discountedPrice: 3600, // £36.00 in pence for professional subscribers
    vatRate: 0.20,
    category: 'course',
    duration: '60 hours',
    level: 'Intermediate'
  },

  // ===== ONE-TIME BOOK PURCHASES =====
  {
    id: 'book_engineering',
    type: 'book',
    name: 'Engineering Handbook 2024',
    description: 'Comprehensive engineering reference books',
    basePrice: 1200, // £12.00 in pence (before VAT)
    vat: 240, // 20% VAT in pence
    total: 1440, // Total with VAT in pence
    amountInPence: 1440,
    currency: 'GBP',
    icon: 'book-open-variant',
    popular: true,
    features: [
      'Comprehensive engineering reference',
      'Updated industry standards',
      'Digital and print versions',
      'Searchable content',
      'Regular updates included',
      'Mobile app access',
      'Offline reading capability',
      'Bookmark and note features'
    ],
    discounts: {
      professional: 300, // £3 discount in pence
      business: 300      // £3 discount in pence
    },
    discountedPrice: 900, // £9.00 in pence for subscribers
    vatRate: 0.20,
    category: 'book',
    pages: 850,
    format: 'Digital + Print'
  },

  {
    id: 'book_design',
    type: 'book',
    name: 'Professional Design Manual',
    description: 'Professional design and engineering manual',
    basePrice: 2500, // £25.00 in pence (before VAT)
    vat: 500, // 20% VAT in pence
    total: 3000, // Total with VAT in pence
    amountInPence: 3000,
    currency: 'GBP',
    icon: 'book',
    popular: false,
    features: [
      'Professional design principles',
      'Industry best practices',
      'Case studies and examples',
      'Digital resources included',
      'Expert author insights',
      'Template downloads',
      'Video tutorials',
      'Community access'
    ],
    discounts: {
      professional: 500, // £5 discount in pence
      business: 625      // £6.25 discount in pence
    },
    discountedPrice: 2000, // £20.00 in pence for professional subscribers
    vatRate: 0.20,
    category: 'book',
    pages: 450,
    format: 'Digital'
  },

  {
    id: 'book_sustainability',
    type: 'book',
    name: 'Sustainable Engineering Practices',
    description: 'Guide to sustainable and green engineering',
    basePrice: 1800, // £18.00 in pence (before VAT)
    vat: 360, // 20% VAT in pence
    total: 2160, // Total with VAT in pence
    amountInPence: 2160,
    currency: 'GBP',
    icon: 'leaf',
    popular: true,
    features: [
      'Environmental impact assessment',
      'Green building standards',
      'Renewable energy systems',
      'Waste reduction strategies',
      'Sustainability certifications',
      'Carbon footprint calculation'
    ],
    discounts: {
      professional: 360, // £3.60 discount in pence
      business: 450      // £4.50 discount in pence
    },
    discountedPrice: 1440, // £14.40 in pence for professional subscribers
    vatRate: 0.20,
    category: 'book',
    pages: 320,
    format: 'Digital + Print'
  },

  // ===== ONE-TIME SERVICE PAYMENTS =====
  {
    id: 'service_cv_review',
    type: 'service',
    name: 'Professional CV Review',
    description: 'Expert CV review and optimization service',
    basePrice: 2500, // £25.00 in pence (before VAT)
    vat: 500, // 20% VAT in pence
    total: 3000, // Total with VAT in pence
    amountInPence: 3000,
    currency: 'GBP',
    icon: 'file-document-edit',
    popular: true,
    features: [
      'Professional CV analysis',
      'Industry-specific recommendations',
      'ATS optimization',
      '48-hour turnaround',
      'One revision included',
      'LinkedIn profile optimization',
      'Cover letter template',
      'Interview tips included'
    ],
    vatRate: 0.20,
    category: 'service',
    deliveryTime: '48 hours',
    revisions: 1
  },

  {
    id: 'service_interview',
    type: 'service', 
    name: 'Interview Coaching Session',
    description: '1-on-1 interview preparation and coaching',
    basePrice: 5000, // £50.00 in pence (before VAT)
    vat: 1000, // 20% VAT in pence
    total: 6000, // Total with VAT in pence
    amountInPence: 6000,
    currency: 'GBP',
    icon: 'account-voice',
    popular: false,
    features: [
      '60-minute coaching session',
      'Mock interview practice',
      'Personalized feedback',
      'Industry-specific preparation',
      'Follow-up resources',
      'Video call or in-person',
      'Recording provided',
      'Action plan included'
    ],
    vatRate: 0.20,
    category: 'service',
    duration: '60 minutes',
    format: 'Video/In-person'
  },

  {
    id: 'service_career',
    type: 'service',
    name: 'Career Consultation',
    description: 'Comprehensive career planning and guidance',
    basePrice: 7500, // £75.00 in pence (before VAT)
    vat: 1500, // 20% VAT in pence
    total: 9000, // Total with VAT in pence
    amountInPence: 9000,
    currency: 'GBP',
    icon: 'account-tie',
    popular: false,
    features: [
      '90-minute consultation',
      'Career path analysis',
      'Skills assessment',
      'Industry insights',
      'Action plan development',
      'Salary negotiation tips',
      'Network building strategies',
      '30-day follow-up included'
    ],
    vatRate: 0.20,
    category: 'service',
    duration: '90 minutes',
    followUp: '30 days'
  },

  {
    id: 'service_portfolio',
    type: 'service',
    name: 'Portfolio Development',
    description: 'Professional portfolio creation and optimization',
    basePrice: 12500, // £125.00 in pence (before VAT)
    vat: 2500, // 20% VAT in pence
    total: 15000, // Total with VAT in pence
    amountInPence: 15000,
    currency: 'GBP',
    icon: 'briefcase',
    popular: true,
    features: [
      'Custom portfolio website',
      'Project showcase design',
      'Professional photography',
      'Content writing assistance',
      'SEO optimization',
      'Mobile responsive design',
      'Domain and hosting setup',
      '6 months support included'
    ],
    vatRate: 0.20,
    category: 'service',
    deliveryTime: '2 weeks',
    support: '6 months'
  },

  // ===== ONE-TIME CERTIFICATION PAYMENTS =====
  {
    id: 'cert_professional',
    type: 'certification',
    name: 'Professional Engineering Certification',
    description: 'Official professional engineering certification',
    basePrice: 15000, // £150.00 in pence (before VAT)
    vat: 3000, // 20% VAT in pence
    total: 18000, // Total with VAT in pence
    amountInPence: 18000,
    currency: 'GBP',
    icon: 'certificate',
    popular: true,
    features: [
      'Industry-recognized certification',
      'Digital badge and certificate',
      'CPD points included',
      'Lifetime validity',
      'Professional network access',
      'Annual conference access',
      'Technical resources library',
      'Mentorship program eligibility'
    ],
    vatRate: 0.20,
    category: 'certification',
    validity: 'Lifetime',
    cpdPoints: 40
  },

  {
    id: 'cert_project_management',
    type: 'certification',
    name: 'Project Management Certification',
    description: 'PMP-aligned project management certification',
    basePrice: 8500, // £85.00 in pence (before VAT)
    vat: 1700, // 20% VAT in pence
    total: 10200, // Total with VAT in pence
    amountInPence: 10200,
    currency: 'GBP',
    icon: 'clipboard-check',
    popular: false,
    features: [
      'PMP-aligned curriculum',
      'Exam preparation materials',
      'Practice tests included',
      'Study group access',
      'Expert instructor support',
      'Certificate upon completion',
      'PDU credits included',
      'Career advancement support'
    ],
    vatRate: 0.20,
    category: 'certification',
    examIncluded: true,
    pduCredits: 35
  },

  {
    id: 'cert_sustainability',
    type: 'certification',
    name: 'Sustainable Engineering Certification',
    description: 'Green and sustainable engineering practices certification',
    basePrice: 6500, // £65.00 in pence (before VAT)
    vat: 1300, // 20% VAT in pence
    total: 7800, // Total with VAT in pence
    amountInPence: 7800,
    currency: 'GBP',
    icon: 'leaf-circle',
    popular: true,
    features: [
      'LEED-aligned content',
      'Environmental impact assessment',
      'Green building standards',
      'Renewable energy focus',
      'Sustainability reporting',
      'Industry case studies',
      'Expert mentorship',
      'Global recognition'
    ],
    vatRate: 0.20,
    category: 'certification',
    alignment: 'LEED',
    recognition: 'Global'
  }
];

// Payment calculation utilities
export const PaymentUtils = {
  // Calculate price with discount
  calculateDiscountedPrice: (scenario, userSubscription = null) => {
    if (!scenario.discounts || !userSubscription) {
      return scenario.basePrice;
    }
    
    const discount = scenario.discounts[userSubscription];
    if (discount) {
      return scenario.basePrice - discount;
    }
    
    return scenario.basePrice;
  },

  // Calculate final price with VAT and discounts
  calculateFinalPrice: (scenario, userSubscription = null) => {
    const discountedPrice = PaymentUtils.calculateDiscountedPrice(scenario, userSubscription);
    const vat = Math.round(discountedPrice * scenario.vatRate);
    const total = discountedPrice + vat;
    
    return {
      ...scenario,
      basePrice: discountedPrice,
      vat: vat,
      total: total,
      amountInPence: total,
      userSubscription: userSubscription,
      hasDiscount: scenario.discounts && userSubscription && scenario.discounts[userSubscription] > 0
    };
  },

  // Convert pence to pounds
  toPounds: (pence) => {
    return (pence / 100).toFixed(2);
  },

  // Convert pounds to pence
  toPence: (pounds) => {
    return Math.round(pounds * 100);
  },

  // Format currency display
  formatCurrency: (amountInPence, currency = 'GBP') => {
    const symbol = currency === 'GBP' ? '£' : currency;
    return `${symbol}${(amountInPence / 100).toFixed(2)}`;
  },

  // Get payment scenario by ID
  getScenario: (scenarioId) => {
    return PAYMENT_SCENARIOS.find(s => s.id === scenarioId);
  },

  // Get scenarios by type
  getScenariosByType: (type) => {
    return PAYMENT_SCENARIOS.filter(s => s.type === type);
  },

  // Get scenarios by category
  getScenariosByCategory: (category) => {
    return PAYMENT_SCENARIOS.filter(s => s.category === category);
  },

  // Get all subscription scenarios
  getSubscriptionScenarios: () => {
    return PAYMENT_SCENARIOS.filter(s => s.type === 'subscription');
  },

  // Get all one-time payment scenarios
  getOneTimeScenarios: () => {
    return PAYMENT_SCENARIOS.filter(s => s.type !== 'subscription');
  },

  // Get popular scenarios
  getPopularScenarios: () => {
    return PAYMENT_SCENARIOS.filter(s => s.popular === true);
  },

  // Get scenarios with discounts
  getDiscountableScenarios: () => {
    return PAYMENT_SCENARIOS.filter(s => s.discounts && Object.keys(s.discounts).length > 0);
  },

  // Calculate total savings for a user subscription
  calculateTotalSavings: (userSubscription) => {
    const discountableScenarios = PaymentUtils.getDiscountableScenarios();
    let totalSavings = 0;
    
    discountableScenarios.forEach(scenario => {
      if (scenario.discounts[userSubscription]) {
        totalSavings += scenario.discounts[userSubscription];
      }
    });
    
    return totalSavings;
  },

  // Get recommended scenarios based on user type
  getRecommendedScenarios: (userType = 'professional') => {
    const recommendations = {
      professional: ['prof_sub_monthly', 'course_engineering', 'service_cv_review', 'cert_professional'],
      business: ['biz_sub_monthly', 'course_project_management', 'service_portfolio', 'cert_project_management'],
      student: ['course_engineering', 'book_engineering', 'service_cv_review', 'cert_sustainability']
    };
    
    return recommendations[userType] || recommendations.professional;
  }
};

// Payment navigation helper
export const PaymentNavigation = {
  // Navigate to payment with scenario
  navigateToPayment: (navigation, scenarioId, userSubscription = null, additionalParams = {}) => {
    const scenario = PaymentUtils.getScenario(scenarioId);
    
    if (!scenario) {
      console.error('Payment scenario not found:', scenarioId);
      return;
    }

    const finalScenario = PaymentUtils.calculateFinalPrice(scenario, userSubscription);

    navigation.navigate('PaymentScreen', {
      scenario: finalScenario,
      ...additionalParams
    });
  },

  // Navigate to payment scenarios with filters
  navigateToPaymentScenarios: (navigation, filters = {}) => {
    navigation.navigate('PaymentScenariosScreen', {
      filters,
      userSubscriptionType: filters.userSubscription || null
    });
  }
};

// Payment categories configuration
export const PAYMENT_CATEGORIES = {
  subscriptions: {
    id: 'subscriptions',
    name: 'Monthly Plans',
    icon: 'crown',
    color: '#4285F4',
    description: 'Professional and Business subscriptions'
  },
  courses: {
    id: 'courses',
    name: 'Courses',
    icon: 'school',
    color: '#16a34a',
    description: 'Engineering and technical courses'
  },
  books: {
    id: 'books',
    name: 'Books',
    icon: 'book-open-variant',
    color: '#dc2626',
    description: 'Professional development books'
  },
  services: {
    id: 'services',
    name: 'Services',
    icon: 'account-tie',
    color: '#7c3aed',
    description: 'Career coaching and consultation'
  },
  certifications: {
    id: 'certifications',
    name: 'Certifications',
    icon: 'certificate',
    color: '#ea580c',
    description: 'Professional certifications'
  }
};

// Export default for backward compatibility
export default PAYMENT_SCENARIOS;

